import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Plus, Edit } from 'lucide-react';
import { salesDistributionAPI, companyAPI, assignmentsAPI, materialManagementAPI, masterDataAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';

const SalesDistribution = () => {
  const { canWrite } = useAuth();
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') || 'sales-orgs';
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  const [salesOrgs, setSalesOrgs] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [distChannels, setDistChannels] = useState([]);
  const [salesOffices, setSalesOffices] = useState([]);
  const [salesGroups, setSalesGroups] = useState([]);
  const [salesAreas, setSalesAreas] = useState([]);
  const [companyCodes, setCompanyCodes] = useState([]);
  const [departureZones, setDepartureZones] = useState([]);
  const [shippingPoints, setShippingPoints] = useState([]);
  const [countries, setCountries] = useState([]);
  const [factoryCalendars, setFactoryCalendars] = useState([]);
  const [plants, setPlants] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  
  // Assignment data for validation
  const [distChannelAssignments, setDistChannelAssignments] = useState([]);
  const [divisionAssignments, setDivisionAssignments] = useState([]);
  
  // Form state for all dropdowns
  const [selectedParent, setSelectedParent] = useState('');
  const [selectedSalesOrg, setSelectedSalesOrg] = useState('');
  const [selectedDistChannel, setSelectedDistChannel] = useState('');
  const [selectedDivision, setSelectedDivision] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedDepartureZone, setSelectedDepartureZone] = useState('');
  const [selectedFactoryCalendar, setSelectedFactoryCalendar] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('Active');

  useEffect(() => {
    setActiveTab(tabFromUrl);
  }, [tabFromUrl]);
  
  useEffect(() => {
    // Reset form state when editing item changes
    if (editingItem) {
      setSelectedParent(editingItem.company_code_id || editingItem.sales_area_id || editingItem.sales_office_id || '');
      setSelectedSalesOrg(editingItem.sales_org_id || '');
      setSelectedDistChannel(editingItem.dist_channel_id || '');
      setSelectedDivision(editingItem.division_id || '');
      setSelectedCountry(editingItem.country_key || '');
      setSelectedDepartureZone(editingItem.departure_zone_id || '');
      setSelectedFactoryCalendar(editingItem.factory_calendar_id || '');
      setSelectedStatus(editingItem.status || 'Active');
    } else {
      setSelectedParent('');
      setSelectedSalesOrg('');
      setSelectedDistChannel('');
      setSelectedDivision('');
      setSelectedCountry('');
      setSelectedDepartureZone('');
      setSelectedFactoryCalendar('');
      setSelectedStatus('Active');
    }
  }, [editingItem]);

  useEffect(() => {
    console.log('🚀 SalesDistribution component mounted, calling loadData...');
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    console.log('🔄 SalesDistribution: Starting loadData...');
    try {
      console.log('📡 Calling all APIs...');
      const [orgs, divs, channels, offices, groups, areas, codes, dcAssignments, divAssignments, zones, points, countryData, calendars, plantData] = await Promise.allSettled([
        salesDistributionAPI.getSalesOrganizations(),
        salesDistributionAPI.getDivisions(),
        salesDistributionAPI.getDistributionChannels(),
        salesDistributionAPI.getSalesOffices(),
        salesDistributionAPI.getSalesGroups(),
        salesDistributionAPI.getSalesAreas(),
        companyAPI.getCompanyCodes(),
        assignmentsAPI.getSalesOrgDistChannels(),
        assignmentsAPI.getSalesOrgDivisions(),
        salesDistributionAPI.getDepartureZones(),
        salesDistributionAPI.getShippingPoints(),
        masterDataAPI.getCountries(),
        materialManagementAPI.getFactoryCalendars(),
        materialManagementAPI.getPlants(),
      ]);
      console.log('✅ All API calls completed');
      
      // Set data for successful requests, use empty array for failed ones
      console.log('📊 Setting state data...');
      setSalesOrgs(orgs.status === 'fulfilled' ? orgs.value.data : []);
      console.log('  - Sales Orgs:', orgs.status === 'fulfilled' ? orgs.value.data.length : 'FAILED');
      
      setDivisions(divs.status === 'fulfilled' ? divs.value.data : []);
      console.log('  - Divisions:', divs.status === 'fulfilled' ? divs.value.data.length : 'FAILED');
      
      setDistChannels(channels.status === 'fulfilled' ? channels.value.data : []);
      console.log('  - Dist Channels:', channels.status === 'fulfilled' ? channels.value.data.length : 'FAILED');
      
      setSalesOffices(offices.status === 'fulfilled' ? offices.value.data : []);
      console.log('  - Sales Offices:', offices.status === 'fulfilled' ? offices.value.data.length : 'FAILED');
      
      setSalesGroups(groups.status === 'fulfilled' ? groups.value.data : []);
      console.log('  - Sales Groups:', groups.status === 'fulfilled' ? groups.value.data.length : 'FAILED');
      
      setSalesAreas(areas.status === 'fulfilled' ? areas.value.data : []);
      console.log('  - Sales Areas:', areas.status === 'fulfilled' ? areas.value.data.length : 'FAILED');
      
      setCompanyCodes(codes.status === 'fulfilled' ? codes.value.data : []);
      console.log('  - Company Codes:', codes.status === 'fulfilled' ? codes.value.data.length : 'FAILED');
      
      setDistChannelAssignments(dcAssignments.status === 'fulfilled' ? dcAssignments.value.data : []);
      console.log('  - DC Assignments:', dcAssignments.status === 'fulfilled' ? dcAssignments.value.data.length : 'FAILED');
      
      setDivisionAssignments(divAssignments.status === 'fulfilled' ? divAssignments.value.data : []);
      console.log('  - Div Assignments:', divAssignments.status === 'fulfilled' ? divAssignments.value.data.length : 'FAILED');
      
      setDepartureZones(zones.status === 'fulfilled' ? zones.value.data : []);
      console.log('  - Departure Zones:', zones.status === 'fulfilled' ? zones.value.data.length : 'FAILED');
      
      setShippingPoints(points.status === 'fulfilled' ? points.value.data : []);
      console.log('  - Shipping Points:', points.status === 'fulfilled' ? points.value.data.length : 'FAILED');
      
      setCountries(countryData.status === 'fulfilled' ? countryData.value.data : []);
      console.log('  - Countries:', countryData.status === 'fulfilled' ? countryData.value.data.length : 'FAILED');
      
      setFactoryCalendars(calendars.status === 'fulfilled' ? calendars.value.data : []);
      console.log('  - Factory Calendars:', calendars.status === 'fulfilled' ? calendars.value.data.length : 'FAILED');
      
      setPlants(plantData.status === 'fulfilled' ? plantData.value.data : []);
      console.log('  - Plants:', plantData.status === 'fulfilled' ? plantData.value.data.length : 'FAILED');
      
      // Log any failures with details
      const apiNames = ['Sales Orgs', 'Divisions', 'Dist Channels', 'Sales Offices', 'Sales Groups', 'Sales Areas', 'Company Codes', 'DC Assignments', 'Div Assignments', 'Departure Zones', 'Shipping Points', 'Countries', 'Factory Calendars', 'Plants'];
      const results = [orgs, divs, channels, offices, groups, areas, codes, dcAssignments, divAssignments, zones, points, countryData, calendars, plantData];
      
      const failures = results
        .map((result, index) => ({ name: apiNames[index], result }))
        .filter(item => item.result.status === 'rejected');
      
      if (failures.length > 0) {
        console.error('❌ Failed API calls:');
        failures.forEach(failure => {
          console.error(`  - ${failure.name}:`, failure.result.reason);
        });
        toast.warning(`Loaded data with ${failures.length} warning(s). Check console for details.`);
      } else {
        console.log('✅ All data loaded successfully!');
      }
    } catch (error) {
      console.error('❌ Critical error in loadData:', error);
      toast.error('Failed to load data: ' + error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = {};
    formData.forEach((value, key) => {
      // Convert empty strings to null for optional fields
      data[key] = value === '' ? null : value;
    });

    // Additional validation for departure zones
    if (activeTab === 'departure-zones') {
      if (data.valid_from && data.valid_to) {
        const validFrom = new Date(data.valid_from);
        const validTo = new Date(data.valid_to);
        
        if (validTo < validFrom) {
          toast.error('Valid To date must be equal to or after Valid From date');
          return;
        }
      }
      
      if (!data.valid_to) {
        toast.error('Valid To date is required');
        return;
      }
    }

    // Additional validation for shipping points
    if (activeTab === 'shipping-points' && !editingItem) {
      const requiredFields = [
        { key: 'shipping_point_id', label: 'Shipping Point ID' },
        { key: 'name', label: 'Name' },
        { key: 'country_key', label: 'Country' },
        { key: 'departure_zone_id', label: 'Departure Zone' },
        { key: 'pick_pack_time_days', label: 'Pick/Pack Time' },
        { key: 'load_time_days', label: 'Load Time' },
        { key: 'factory_calendar_id', label: 'Factory Calendar' },
        { key: 'status', label: 'Status' }
      ];

      const missingFields = requiredFields.filter(field => !data[field.key] || data[field.key] === '');
      
      if (missingFields.length > 0) {
        const fieldNames = missingFields.map(f => f.label).join(', ');
        toast.error(`Please fill all required fields: ${fieldNames}`);
        return;
      }

      // Validate numeric fields
      if (data.pick_pack_time_days < 0 || data.load_time_days < 0) {
        toast.error('Pick/Pack Time and Load Time must be non-negative');
        return;
      }
    }

    try {
      if (editingItem) {
        await updateItem(data);
      } else {
        await createItem(data);
      }
      setIsDialogOpen(false);
      setEditingItem(null);
      loadData();
      toast.success(editingItem ? 'Updated successfully' : 'Created successfully');
    } catch (error) {
      console.error('Error during form submission:', error);
      
      // Handle different error formats
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // If detail is an array (validation errors from FastAPI)
        if (Array.isArray(detail)) {
          const errorMessages = detail.map(err => {
            const field = err.loc ? err.loc.join(' > ') : 'Unknown field';
            return `${field}: ${err.msg}`;
          }).join('; ');
          toast.error(`Validation Error: ${errorMessages}`);
        } 
        // If detail is a string
        else if (typeof detail === 'string') {
          toast.error(detail);
        } 
        // Fallback
        else {
          toast.error('Operation failed. Please check your inputs.');
        }
      } else {
        toast.error(error.message || 'Operation failed. Please try again.');
      }
    }
  };

  const createItem = async (data) => {
    switch (activeTab) {
      case 'sales-orgs':
        await salesDistributionAPI.createSalesOrganization(data);
        break;
      case 'divisions':
        await salesDistributionAPI.createDivision(data);
        break;
      case 'dist-channels':
        await salesDistributionAPI.createDistributionChannel(data);
        break;
      case 'sales-areas':
        await salesDistributionAPI.createSalesArea(data);
        break;
      case 'sales-offices':
        await salesDistributionAPI.createSalesOffice(data);
        break;
      case 'sales-groups':
        await salesDistributionAPI.createSalesGroup(data);
        break;
      case 'departure-zones':
        await salesDistributionAPI.createDepartureZone(data);
        break;
      case 'shipping-points':
        await salesDistributionAPI.createShippingPoint(data);
        break;
    }
  };

  const updateItem = async (data) => {
    const id = editingItem[getIdField()];
    switch (activeTab) {
      case 'sales-orgs':
        await salesDistributionAPI.updateSalesOrganization(id, data);
        break;
      case 'divisions':
        await salesDistributionAPI.updateDivision(id, data);
        break;
      case 'dist-channels':
        await salesDistributionAPI.updateDistributionChannel(id, data);
        break;
      case 'sales-areas':
        await salesDistributionAPI.updateSalesArea(id, data);
        break;
      case 'sales-offices':
        await salesDistributionAPI.updateSalesOffice(id, data);
        break;
      case 'sales-groups':
        await salesDistributionAPI.updateSalesGroup(id, data);
        break;
      case 'departure-zones':
        await salesDistributionAPI.updateDepartureZone(editingItem.country_key, editingItem.departure_zone_id, data);
        break;
      case 'shipping-points':
        await salesDistributionAPI.updateShippingPoint(id, data);
        break;
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const id = item[getIdField()];
      switch (activeTab) {
        case 'sales-orgs':
          await salesDistributionAPI.deleteSalesOrganization(id);
          break;
        case 'divisions':
          await salesDistributionAPI.deleteDivision(id);
          break;
        case 'dist-channels':
          await salesDistributionAPI.deleteDistributionChannel(id);
          break;
        case 'sales-areas':
          await salesDistributionAPI.deleteSalesArea(id);
          break;
        case 'sales-offices':
          await salesDistributionAPI.deleteSalesOffice(id);
          break;
        case 'sales-groups':
          await salesDistributionAPI.deleteSalesGroup(id);
          break;
        case 'departure-zones':
          await salesDistributionAPI.deleteDepartureZone(item.country_key, item.departure_zone_id);
          break;
        case 'shipping-points':
          await salesDistributionAPI.deleteShippingPoint(id);
          break;
      }
      loadData();
      toast.success('Deleted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const getIdField = () => {
    switch (activeTab) {
      case 'sales-orgs': return 'sales_org_id';
      case 'divisions': return 'division_id';
      case 'dist-channels': return 'dist_channel_id';
      case 'sales-areas': return 'sales_area_id';
      case 'sales-offices': return 'sales_office_id';
      case 'sales-groups': return 'sales_group_id';
      case 'departure-zones': return 'departure_zone_id';
      case 'shipping-points': return 'shipping_point_id';
      default: return 'id';
    }
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case 'sales-orgs': return salesOrgs;
      case 'divisions': return divisions;
      case 'dist-channels': return distChannels;
      case 'sales-areas': return salesAreas;
      case 'sales-offices': return salesOffices;
      case 'sales-groups': return salesGroups;
      case 'departure-zones': return departureZones;
      case 'shipping-points': return shippingPoints;
      default: return [];
    }
  };

  const getParentRequired = () => {
    switch (activeTab) {
      case 'sales-orgs': return companyCodes.length === 0 ? 'Company Codes' : null;
      case 'sales-areas': return (salesOrgs.length === 0 || divisions.length === 0 || distChannels.length === 0) ? 'Sales Organizations, Divisions, and Distribution Channels' : null;
      case 'departure-zones': return countries.length === 0 ? 'Countries' : null;
      case 'shipping-points': return (countries.length === 0 || departureZones.length === 0 || factoryCalendars.length === 0) ? 'Countries, Departure Zones, and Factory Calendars' : null;
      default: return null;
    }
  };

  const getDisplayText = (item) => {
    switch (activeTab) {
      case 'sales-orgs':
        return (
          <div>
            <div className="font-medium">{item.sales_org_name}</div>
            <div className="text-xs text-gray-500 mt-0.5">Company Code: {item.company_code_id}</div>
          </div>
        );
      case 'divisions':
        return (
          <div>
            <div className="font-medium">{item.division_name}</div>
          </div>
        );
      case 'dist-channels':
        return (
          <div>
            <div className="font-medium">{item.dist_channel_name}</div>
          </div>
        );
      case 'sales-areas':
        return (
          <div>
            <div className="font-medium">{item.sales_org_id} / {item.dist_channel_id} / {item.division_id}</div>
            <div className="text-xs text-gray-500 mt-0.5">
              Sales Org: {item.sales_org_id} | Dist Channel: {item.dist_channel_id} | Division: {item.division_id}
            </div>
          </div>
        );
      case 'sales-offices':
        return (
          <div>
            <div className="font-medium">{item.sales_office_name}</div>
          </div>
        );
      case 'sales-groups':
        return (
          <div>
            <div className="font-medium">{item.sales_group_name}</div>
          </div>
        );
      case 'departure-zones':
        return (
          <div>
            <div className="font-medium">{item.name || '-'}</div>
            <div className="text-xs text-gray-500 mt-0.5">
              Country: {item.country_key || '-'} | Status: <span className={item.status === 'Active' ? 'text-green-600' : 'text-red-600'}>{item.status || '-'}</span>
              {item.region_code && ` | Region: ${item.region_code}`}
            </div>
            <div className="text-xs text-gray-500">
              Valid: {item.valid_from ? String(item.valid_from) : '-'} to {item.valid_to ? String(item.valid_to) : 'Open'}
            </div>
          </div>
        );
      case 'shipping-points':
        return (
          <div>
            <div className="font-medium">{item.name || '-'}</div>
            <div className="text-xs text-gray-500 mt-0.5">
              Country: {item.country_key || '-'} | Zone: {item.departure_zone_id || '-'} | Status: <span className={item.status === 'Active' ? 'text-green-600' : 'text-red-600'}>{item.status || '-'}</span>
            </div>
            <div className="text-xs text-gray-500">
              Pick/Pack: {item.pick_pack_time_days ?? 0}d | Load: {item.load_time_days ?? 0}d | Calendar: {item.factory_calendar_id || '-'}
            </div>
          </div>
        );
      default:
        return '-';
    }
  };

  const getFormFields = () => {
    switch (activeTab) {
      case 'sales-orgs':
        return (
          <React.Fragment>
            <div>
              <Label>Sales Organization ID</Label>
              <Input 
                name="sales_org_id" 
                defaultValue={editingItem?.sales_org_id} 
                required 
                disabled={!!editingItem} 
                placeholder="e.g., SO001"
                onChange={(e) => e.target.value = e.target.value.toUpperCase()}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div>
              <Label>Sales Organization Name</Label>
              <Input name="sales_org_name" defaultValue={editingItem?.sales_org_name} required placeholder="e.g., North America Sales" />
            </div>
            <div>
              <Label>Company Code</Label>
              {companyCodes.length === 0 ? (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
                  Please create a Company Code first
                </div>
              ) : (
                <React.Fragment>
                  <Select value={selectedParent} onValueChange={setSelectedParent} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select company code" />
                    </SelectTrigger>
                    <SelectContent>
                      {companyCodes.map(cc => (
                        <SelectItem key={cc.company_code_id} value={cc.company_code_id}>
                          {cc.company_code_id} - {cc.company_code_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <input type="hidden" name="company_code_id" value={selectedParent} />
                </React.Fragment>
              )}
            </div>
          </React.Fragment>
        );
      case 'divisions':
        return (
          <React.Fragment>
            <div>
              <Label>Division ID</Label>
              <Input 
                name="division_id" 
                defaultValue={editingItem?.division_id} 
                required 
                disabled={!!editingItem} 
                placeholder="e.g., D001"
                onChange={(e) => e.target.value = e.target.value.toUpperCase()}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div>
              <Label>Division Name</Label>
              <Input name="division_name" defaultValue={editingItem?.division_name} required placeholder="e.g., Electronics" />
            </div>
            <div className="p-3 bg-orange-50 border border-orange-200 rounded text-sm text-orange-700">
              <strong>Note:</strong> Assign this Division to Sales Organizations in the <strong>Assignments</strong> page after creation.
            </div>
          </React.Fragment>
        );
      case 'dist-channels':
        return (
          <React.Fragment>
            <div>
              <Label>Distribution Channel ID</Label>
              <Input 
                name="dist_channel_id" 
                defaultValue={editingItem?.dist_channel_id} 
                required 
                disabled={!!editingItem} 
                placeholder="e.g., DC001"
                onChange={(e) => e.target.value = e.target.value.toUpperCase()}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div>
              <Label>Distribution Channel Name</Label>
              <Input name="dist_channel_name" defaultValue={editingItem?.dist_channel_name} required placeholder="e.g., Retail, Wholesale" />
            </div>
            <div className="p-3 bg-orange-50 border border-orange-200 rounded text-sm text-orange-700">
              <strong>Note:</strong> Assign this Distribution Channel to Sales Organizations in the <strong>Assignments</strong> page after creation.
            </div>
          </React.Fragment>
        );
      case 'sales-areas':
        // Filter Distribution Channels assigned to the selected Sales Organization
        const assignedDistChannelIds = selectedSalesOrg 
          ? distChannelAssignments
              .filter(a => a.sales_org_id === selectedSalesOrg)
              .map(a => a.dist_channel_id)
          : [];
        const filteredDistChannels = selectedSalesOrg
          ? distChannels.filter(dc => assignedDistChannelIds.includes(dc.dist_channel_id))
          : [];
        
        // Filter Divisions assigned to the selected Sales Organization
        const assignedDivisionIds = selectedSalesOrg
          ? divisionAssignments
              .filter(a => a.sales_org_id === selectedSalesOrg)
              .map(a => a.division_id)
          : [];
        const filteredDivisions = selectedSalesOrg
          ? divisions.filter(div => assignedDivisionIds.includes(div.division_id))
          : [];
        
        return (
          <React.Fragment>
            <div>
              <Label>Sales Area ID</Label>
              <Input 
                name="sales_area_id" 
                defaultValue={editingItem?.sales_area_id} 
                required 
                disabled={!!editingItem} 
                placeholder="e.g., SA001"
                onChange={(e) => e.target.value = e.target.value.toUpperCase()}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div>
              <Label>Sales Organization</Label>
              {salesOrgs.length === 0 ? (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
                  Please create a Sales Organization first
                </div>
              ) : (
                <React.Fragment>
                  <Select value={selectedSalesOrg} onValueChange={(value) => {
                    setSelectedSalesOrg(value);
                    setSelectedDistChannel('');
                    setSelectedDivision('');
                  }} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select sales organization" />
                    </SelectTrigger>
                    <SelectContent>
                      {salesOrgs.map(so => (
                        <SelectItem key={so.sales_org_id} value={so.sales_org_id}>
                          {so.sales_org_id} - {so.sales_org_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <input type="hidden" name="sales_org_id" value={selectedSalesOrg} />
                </React.Fragment>
              )}
            </div>
            <div>
              <Label>Distribution Channel</Label>
              {!selectedSalesOrg ? (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
                  Please select a Sales Organization first
                </div>
              ) : filteredDistChannels.length === 0 ? (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
                  No Distribution Channels assigned to this Sales Organization
                </div>
              ) : (
                <React.Fragment>
                  <Select value={selectedDistChannel} onValueChange={setSelectedDistChannel} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select distribution channel" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredDistChannels.map(dc => (
                        <SelectItem key={dc.dist_channel_id} value={dc.dist_channel_id}>
                          {dc.dist_channel_id} - {dc.dist_channel_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <input type="hidden" name="dist_channel_id" value={selectedDistChannel} />
                </React.Fragment>
              )}
            </div>
            <div>
              <Label>Division</Label>
              {!selectedSalesOrg ? (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
                  Please select a Sales Organization first
                </div>
              ) : filteredDivisions.length === 0 ? (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
                  No Divisions assigned to this Sales Organization
                </div>
              ) : (
                <React.Fragment>
                  <Select value={selectedDivision} onValueChange={setSelectedDivision} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select division" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredDivisions.map(div => (
                        <SelectItem key={div.division_id} value={div.division_id}>
                          {div.division_id} - {div.division_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <input type="hidden" name="division_id" value={selectedDivision} />
                </React.Fragment>
              )}
            </div>
            <div className="p-3 bg-orange-50 border border-orange-200 rounded text-sm text-orange-700">
              <strong>Sales Area</strong> is a combination of Sales Organization, Distribution Channel, and Division. 
              <br/>
              <strong>Important:</strong> Only Distribution Channels and Divisions assigned to the selected Sales Organization (via Assignments page) will be available for selection.
            </div>
          </React.Fragment>
        );
      case 'sales-offices':
        return (
          <React.Fragment>
            <div>
              <Label>Sales Office ID</Label>
              <Input 
                name="sales_office_id" 
                defaultValue={editingItem?.sales_office_id} 
                required 
                disabled={!!editingItem} 
                placeholder="e.g., OFF001"
                onChange={(e) => e.target.value = e.target.value.toUpperCase()}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div>
              <Label>Sales Office Name</Label>
              <Input name="sales_office_name" defaultValue={editingItem?.sales_office_name} required placeholder="e.g., Chicago Office" />
            </div>
            <div className="p-3 bg-orange-50 border border-orange-200 rounded text-sm text-orange-700">
              <strong>Note:</strong> Assign this Sales Office to Sales Areas in the <strong>Assignments</strong> page after creation.
            </div>
          </React.Fragment>
        );
      case 'sales-groups':
        return (
          <React.Fragment>
            <div>
              <Label>Sales Group ID</Label>
              <Input 
                name="sales_group_id" 
                defaultValue={editingItem?.sales_group_id} 
                required 
                disabled={!!editingItem} 
                placeholder="e.g., SG001"
                onChange={(e) => e.target.value = e.target.value.toUpperCase()}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div>
              <Label>Sales Group Name</Label>
              <Input name="sales_group_name" defaultValue={editingItem?.sales_group_name} required placeholder="e.g., Enterprise Sales" />
            </div>
            <div className="p-3 bg-orange-50 border border-orange-200 rounded text-sm text-orange-700">
              <strong>Note:</strong> Assign this Sales Group to Sales Offices in the <strong>Assignments</strong> page after creation.
            </div>
          </React.Fragment>
        );
      case 'departure-zones':
        return (
          <React.Fragment>
            <div>
              <Label>Departure Zone ID *</Label>
              <Input 
                name="departure_zone_id" 
                defaultValue={editingItem?.departure_zone_id || ''} 
                required 
                disabled={!!editingItem}
                placeholder="e.g., IN01"
                maxLength={10}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div>
              <Label>Country *</Label>
              <Select value={selectedCountry} onValueChange={setSelectedCountry} required disabled={!!editingItem}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Country" />
                </SelectTrigger>
                <SelectContent>
                  {Array.isArray(countries) && countries.map(country => (
                    <SelectItem key={country.country_code} value={country.country_code}>
                      {country.country_code} - {country.country_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="country_key" value={selectedCountry} />
            </div>
            <div>
              <Label>Name *</Label>
              <Input name="name" defaultValue={editingItem?.name || ''} required placeholder="e.g., South India Zone" maxLength={100} />
            </div>
            <div>
              <Label>Description (Optional)</Label>
              <Textarea name="description" defaultValue={editingItem?.description || ''} placeholder="Long text description (optional)" maxLength={255} rows={3} />
            </div>
            <div>
              <Label>Region Code (Optional)</Label>
              <Input name="region_code" defaultValue={editingItem?.region_code || ''} placeholder="Optional region/state reference" maxLength={10} />
            </div>
            <div>
              <Label>Status *</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className={!selectedStatus ? 'border-red-300' : ''}>
                  <SelectValue placeholder="Select Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
              <input type="hidden" name="status" value={selectedStatus} required />
              {!selectedStatus && <p className="text-xs text-red-500 mt-1">Status is required</p>}
            </div>
            <div>
              <Label>Valid From *</Label>
              <Input 
                type="date" 
                name="valid_from" 
                id="valid_from"
                defaultValue={editingItem?.valid_from || ''} 
                required 
              />
            </div>
            <div>
              <Label>Valid To *</Label>
              <Input 
                type="date" 
                name="valid_to" 
                id="valid_to"
                defaultValue={editingItem?.valid_to || ''} 
                required 
              />
              <p className="text-xs text-gray-500 mt-1">Must be equal to or after Valid From date</p>
            </div>
            <div className="p-3 bg-orange-50 border border-orange-200 rounded text-sm text-orange-700">
              <strong>Note:</strong> Departure Zone ID is unique per country. Only Active zones can be assigned to Shipping Points.
            </div>
          </React.Fragment>
        );
      case 'shipping-points':
        return (
          <React.Fragment>
            <div>
              <Label>Shipping Point ID *</Label>
              <Input 
                name="shipping_point_id" 
                defaultValue={editingItem?.shipping_point_id || ''} 
                required 
                disabled={!!editingItem}
                placeholder="e.g., SP01"
                maxLength={10}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div>
              <Label>Name *</Label>
              <Input name="name" defaultValue={editingItem?.name || ''} required placeholder="e.g., Bangalore Main Dock" maxLength={100} />
            </div>
            <div>
              <Label>Description (Optional)</Label>
              <Textarea name="description" defaultValue={editingItem?.description || ''} placeholder="Longer description (optional)" maxLength={255} rows={3} />
            </div>
            <div>
              <Label>Country *</Label>
              <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                <SelectTrigger className={!selectedCountry ? 'border-red-300' : ''}>
                  <SelectValue placeholder="Select Country" />
                </SelectTrigger>
                <SelectContent>
                  {Array.isArray(countries) && countries.map(country => (
                    <SelectItem key={country.country_code} value={country.country_code}>
                      {country.country_code} - {country.country_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="country_key" value={selectedCountry} required />
              {!selectedCountry && <p className="text-xs text-red-500 mt-1">Country is required</p>}
            </div>
            <div>
              <Label>Departure Zone *</Label>
              <Select value={selectedDepartureZone} onValueChange={setSelectedDepartureZone}>
                <SelectTrigger className={!selectedDepartureZone ? 'border-red-300' : ''}>
                  <SelectValue placeholder="Select Departure Zone" />
                </SelectTrigger>
                <SelectContent>
                  {Array.isArray(departureZones) && departureZones
                    .filter(z => z.country_key === selectedCountry && z.status === 'Active')
                    .map(zone => (
                      <SelectItem key={`${zone.country_key}-${zone.departure_zone_id}`} value={zone.departure_zone_id}>
                        {zone.departure_zone_id} - {zone.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="departure_zone_id" value={selectedDepartureZone} required />
              {!selectedDepartureZone && <p className="text-xs text-red-500 mt-1">Departure Zone is required</p>}
              {selectedCountry && <p className="text-xs text-gray-500 mt-1">Only zones in the selected country are shown</p>}
            </div>
            <div>
              <Label>Pick/Pack Time (Days) *</Label>
              <Input type="number" name="pick_pack_time_days" defaultValue={editingItem?.pick_pack_time_days || 0} required min={0} />
            </div>
            <div>
              <Label>Load Time (Days) *</Label>
              <Input type="number" name="load_time_days" defaultValue={editingItem?.load_time_days || 0} required min={0} />
            </div>
            <div>
              <Label>Factory Calendar *</Label>
              <Select value={selectedFactoryCalendar} onValueChange={setSelectedFactoryCalendar}>
                <SelectTrigger className={!selectedFactoryCalendar ? 'border-red-300' : ''}>
                  <SelectValue placeholder="Select Factory Calendar" />
                </SelectTrigger>
                <SelectContent>
                  {Array.isArray(factoryCalendars) && factoryCalendars.map(cal => (
                    <SelectItem key={cal.factory_calendar_id} value={cal.factory_calendar_id}>
                      {cal.factory_calendar_id} - {cal.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="factory_calendar_id" value={selectedFactoryCalendar} required />
              {!selectedFactoryCalendar && <p className="text-xs text-red-500 mt-1">Factory Calendar is required</p>}
            </div>
            <div>
              <Label>Status *</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className={!selectedStatus ? 'border-red-300' : ''}>
                  <SelectValue placeholder="Select Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
              <input type="hidden" name="status" value={selectedStatus} required />
              {!selectedStatus && <p className="text-xs text-red-500 mt-1">Status is required</p>}
            </div>
            <div className="p-3 bg-orange-50 border border-orange-200 rounded text-sm text-orange-700">
              <strong>Note:</strong> Assign this Shipping Point to Plants in the <strong>Assignments</strong> page after creation.
            </div>
          </React.Fragment>
        );
    }
  };

  const tabs = [
    { id: 'sales-orgs', label: 'Sales Organizations' },
    { id: 'divisions', label: 'Divisions' },
    { id: 'dist-channels', label: 'Distribution Channels' },
    { id: 'sales-areas', label: 'Sales Areas' },
    { id: 'sales-offices', label: 'Sales Offices' },
    { id: 'sales-groups', label: 'Sales Groups' },
    { id: 'departure-zones', label: 'Departure Zones' },
    { id: 'shipping-points', label: 'Shipping Points' },
  ];

  const parentRequired = getParentRequired();

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="sales-distribution-page">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Sales & Distribution</h2>
          <p className="text-sm text-gray-500 mt-1">Manage your sales organizational structure</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg border border-gray-200 mb-6">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setEditingItem(null);
                }}
                className={`px-6 py-4 font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'text-orange-600 border-b-2 border-orange-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <Card className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-900">
              {tabs.find(t => t.id === activeTab)?.label}
            </h3>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              {canWrite(activeTab) && (
                <DialogTrigger asChild>
                  <Button 
                    onClick={() => setEditingItem(null)}
                    data-testid="btn-add-new"
                    disabled={!!parentRequired}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add New
                  </Button>
                </DialogTrigger>
              )}
              <DialogContent className="max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingItem ? 'Edit' : 'Create'} {tabs.find(t => t.id === activeTab)?.label}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {getFormFields()}
                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="flex-1" data-testid="btn-submit-form">
                      {editingItem ? 'Update' : 'Create'}
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        setIsDialogOpen(false);
                        setEditingItem(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {parentRequired && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Please create {parentRequired} first before creating {tabs.find(t => t.id === activeTab)?.label}.
              </p>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">ID</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Name/Description</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {getCurrentData().length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center py-8 text-gray-500">
                      No records found. Click "Add New" to create one.
                    </td>
                  </tr>
                ) : (
                  getCurrentData().map((item) => {
                    // Create unique key for items that can have same ID in different countries
                    const uniqueKey = activeTab === 'departure-zones' 
                      ? `${item.country_key}-${item[getIdField()]}` 
                      : item[getIdField()];
                    
                    return (
                    <tr key={uniqueKey} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 font-mono text-sm">{item[getIdField()]}</td>
                      <td className="py-3 px-4 text-sm">
                        {getDisplayText(item)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex gap-2 justify-end">
                          <RecordActions
                            doctype={activeTab}
                            idForTestId={item[getIdField()]}
                            onView={() => setViewItem(item)}
                            onEdit={() => {
                              setEditingItem(item);
                              // Set parent entity for editing
                              if (activeTab === 'sales-orgs') {
                                setSelectedParent(item.company_code_id || '');
                              } else if (activeTab === 'divisions' || activeTab === 'dist-channels') {
                                setSelectedParent(item.sales_org_id || '');
                              } else if (activeTab === 'sales-offices') {
                                setSelectedParent(item.sales_area_id || '');
                              } else if (activeTab === 'sales-groups') {
                                setSelectedParent(item.sales_office_id || '');
                              }
                              // Set Sales Area specific fields
                              if (activeTab === 'sales-areas') {
                                setSelectedSalesOrg(item.sales_org_id || '');
                                setSelectedDistChannel(item.dist_channel_id || '');
                                setSelectedDivision(item.division_id || '');
                              }
                              setIsDialogOpen(true);
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title={`${tabs.find(t => t.id === activeTab)?.label || 'Record'} Details`}
        record={viewItem}
      />
    </Layout>
  );
};

export default SalesDistribution;
